import React from 'react';
import { cn } from '../../lib/utils';
import { Label } from './Label';

export interface FormFieldProps {
  /** Label text */
  label: string;
  /** ID of the input element for htmlFor */
  htmlFor?: string;
  /** Show error state */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Show required indicator */
  required?: boolean;
  /** Custom className for the container */
  className?: string;
  /** Input/Select component */
  children: React.ReactNode;
}

/**
 * Form field wrapper that combines Label with form inputs
 *
 * @example
 * ```tsx
 * <FormField label="Email" htmlFor="email-input" required>
 *   <Input id="email-input" type="email" />
 * </FormField>
 *
 * <FormField label="Country" error errorMessage="Please select a country">
 *   <Select options={countries} />
 * </FormField>
 * ```
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  error = false,
  errorMessage,
  required = false,
  className,
  children,
}) => {
  return (
    <div className={cn('w-full', className)}>
      <Label htmlFor={htmlFor} required={required} className="mb-2">
        {label}
      </Label>
      {children}
      {error && errorMessage && (
        <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
      )}
    </div>
  );
};

FormField.displayName = 'FormField';
